import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Building2, Home, Brain, Activity, CalendarClock, BedDouble, HandHeart, ArrowRight } from "lucide-react";
import { SITE, WA_MESSAGES } from "@/config/site";
import { CATEGORIES, DURATIONS, FAQS, LOCATIONS, PROVIDERS } from "@/data/content";
import { IMAGES } from "@/data/pages";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import CareMatcher from "@/components/CareMatcher";
import SevenSteps from "@/components/SevenSteps";
import FaqAccordion from "@/components/FaqAccordion";
import ProviderCard from "@/components/ProviderCard";

const CATEGORY_ICONS = { Building2, Home, Brain, Activity, CalendarClock, BedDouble };
const COLOR_CLS = {
  teal: "bg-brand-tealLight text-brand-teal",
  gold: "bg-brand-goldLight text-brand-gold",
  coral: "bg-brand-coralLight text-brand-coral",
  sage: "bg-brand-sageLight text-brand-sage",
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HERO_LINES = [
  <>Find the Right</>,
  <>
    <span className="font-accent italic text-brand-gold">Nursing Home</span> &
  </>,
  <>Senior Care in Penang</>,
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-brand-teal text-white grain">
      <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-brand-gold/15 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-brand-coral/15 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-24 md:pb-24 grid items-center gap-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">
              Independent Directory · Penang, Malaysia
            </p>
          </Reveal>
          <h1 data-testid="hero-heading" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <Reveal delay={0.7}>
            <p data-testid="hero-subheading" className="mt-6 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed">
              Explore nursing homes and elderly care services across Penang. Compare care options, understand estimated
              costs and get free assistance finding facilities that may suit your family's needs.
            </p>
          </Reveal>
          <Reveal delay={0.85} className="mt-8 flex flex-wrap gap-4">
            <a
              href="#matcher"
              data-testid="hero-browse-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm sm:text-base font-semibold text-brand-teal hover:bg-brand-gold hover:text-white hover:-translate-y-0.5 transition-all duration-300"
            >
              Browse Nursing Homes <ArrowRight className="h-4 w-4" />
            </a>
            <WhatsAppCTA testid="hero-whatsapp-cta" message={WA_MESSAGES.homepage} label="Get Free Assistance" variant="coral" />
          </Reveal>
        </div>
        <Reveal delay={0.4} className="relative">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2.5rem] border-2 border-brand-gold/40 rotate-2" aria-hidden="true" />
            <motion.img
              src={IMAGES.heroCaregiver}
              alt="Caregiver gently supporting a smiling elderly woman in a bright Penang care home"
              width="800"
              height="1000"
              loading="eager"
              style={{ y }}
              className="relative rounded-[2.5rem] object-cover w-full h-[420px] sm:h-[520px] shadow-2xl"
            />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-6 -left-4 sm:-left-8 flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-xl"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-sageLight text-brand-sage">
                <HandHeart className="h-6 w-6" />
              </span>
              <span className="text-sm font-semibold text-brand-charcoal leading-snug">
                Free matching assistance
                <span className="block text-xs font-medium text-slate-500">for every family in Penang</span>
              </span>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div data-testid="location-marquee" className="overflow-hidden border-y border-brand-teal/10 bg-white py-4" aria-hidden="true">
    <div className="flex w-max animate-marquee gap-10">
      {[...LOCATIONS, ...LOCATIONS, ...LOCATIONS, ...LOCATIONS].map((l, i) => (
        <span key={i} className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.2em] text-brand-teal/60">
          <MapPin className="h-4 w-4 text-brand-gold" /> {l}
        </span>
      ))}
    </div>
  </div>
);

const SectionHeading = ({ eyebrow, title, copy, center = true }) => (
  <Reveal className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">{eyebrow}</p>
    <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-brand-charcoal">{title}</h2>
    {copy && <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{copy}</p>}
  </Reveal>
);

const HomePage = () => (
  <main data-testid="page-home">
    <Seo
      title="Nursing Homes in Penang | Elderly Care Directory"
      description="Find nursing homes and elderly care homes in Penang, Malaysia. Explore care options, compare facilities, understand costs and get free help finding suitable care."
      path="/"
      jsonLd={faqJsonLd}
    />

    <Hero />
    <Marquee />

    <section id="matcher" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <SectionHeading
        eyebrow="Care Matcher"
        title="Start With What Your Family Needs"
        copy="Choose a location, care type and duration to explore the directory — or message us and we will match you for free."
      />
      <Reveal delay={0.1} className="mt-10">
        <CareMatcher />
      </Reveal>
      <Reveal delay={0.15} className="mt-6 flex flex-wrap justify-center gap-3">
        {DURATIONS.map((d) => (
          <a key={d.value} href={d.href} data-testid={`duration-link-${d.value}`} className="rounded-full border border-brand-teal/20 bg-white px-5 py-2 text-sm font-medium text-brand-teal hover:bg-brand-teal hover:text-white transition-colors duration-200">
            {d.label}
          </a>
        ))}
      </Reveal>
    </section>

    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Browse by Location"
          title="Senior Care Across Penang"
          copy="From George Town to Seberang Perai, we help families explore care options across the island and the mainland."
        />
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
          {LOCATIONS.map((l, i) => (
            <Reveal key={l} delay={i * 0.05}>
              <a
                href="#matcher"
                data-testid={`location-card-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex h-full flex-col items-center gap-2 rounded-2xl border border-slate-100 bg-brand-cream px-3 py-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-[0_12px_40px_rgb(7,91,89,0.12)] transition-all duration-300"
              >
                <MapPin className="h-6 w-6 text-brand-coral transition-transform duration-300 group-hover:scale-110" />
                <span className="text-sm font-semibold text-brand-charcoal">{l}</span>
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-center text-xs text-slate-400">
            Individual area guides will be published as we verify enough locally relevant content for each neighbourhood.
          </p>
        </Reveal>
      </div>
    </section>

    <section id="care-services" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <SectionHeading
        eyebrow="Browse by Care Category"
        title="Every Kind of Elderly Care, Explained"
        copy="Understand the options before you decide — each guide explains what to expect and what to ask providers."
      />
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORIES.map((c, i) => {
          const Icon = CATEGORY_ICONS[c.icon];
          return (
            <Reveal key={c.title} delay={i * 0.06}>
              <a
                href={c.href}
                data-testid={`category-card-${i + 1}`}
                className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgb(7,91,89,0.14)] transition-all duration-300"
              >
                <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${COLOR_CLS[c.color]} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-brand-charcoal group-hover:text-brand-teal transition-colors">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.description}</p>
                <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold">
                  Explore guide <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
            </Reveal>
          );
        })}
      </div>
    </section>

    <section id="directory" className="scroll-mt-24 bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Featured Providers"
          title="Verified Nursing Homes & Care Facilities"
          copy="Every listing in our directory is verified before publication — names, addresses, care types, fees and photographs — and carries a last-verified date."
        />
        <Reveal delay={0.1} className="mt-10">
          {PROVIDERS.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {PROVIDERS.map((p) => (
                <ProviderCard key={p.id} provider={p} />
              ))}
            </div>
          ) : (
            <div data-testid="directory-invitation" className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-slate-100 bg-brand-cream p-8 sm:p-10">
                <h3 className="font-display text-xl font-semibold text-brand-charcoal">We are verifying our first listings</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                  We never invent facilities, reviews, prices or addresses. While verification is underway, tell us what
                  your family needs and we will help you explore suitable options directly — our matching and
                  coordination assistance is free for families.
                </p>
                <WhatsAppCTA testid="directory-whatsapp-cta" message={WA_MESSAGES.homepage} label="Get Free Assistance" className="mt-6" />
              </div>
              <div className="rounded-3xl bg-brand-teal p-8 sm:p-10 text-white grain relative overflow-hidden">
                <div className="absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-brand-gold/20 blur-3xl" aria-hidden="true" />
                <h3 className="relative font-display text-xl font-semibold">Are you a Penang senior care provider?</h3>
                <p className="relative mt-3 text-sm sm:text-base text-white/80 leading-relaxed">
                  Submit your facility for verification and listing. We publish accurate details — address, confirmed
                  care types, room and fee information — with a last-verified date, and providers may request
                  corrections at any time.
                </p>
                <WhatsAppCTA testid="list-facility-cta" message={WA_MESSAGES.listFacility} label="Submit Listing for Verification" variant="gold" className="relative mt-6" />
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </section>

    <section id="about" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">Free Nursing Home Matching Assistance for Families</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-brand-charcoal">
            Finding the Right Home Shouldn't Be Overwhelming
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">
            Nursing Home in Penang helps families explore suitable care options based on their preferred location, care
            requirements, budget and admission timeline. Our matching and coordination assistance is free for families —
            we help you communicate with potential care providers, arrange viewing appointments and understand the
            admission process.
          </p>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            The selected nursing home or care facility will charge its own accommodation and care fees. Families should
            confirm all charges, deposits, services and payment terms directly with the facility before admission.
          </p>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed">
            We are an independent, brand-neutral directory. We do not own or operate the facilities listed, and we do
            not promise that every facility is available or that admission is guaranteed.
          </p>
          <WhatsAppCTA testid="about-whatsapp-cta" className="mt-7" />
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div className="absolute -inset-3 rounded-[2rem] bg-brand-sageLight -rotate-2" aria-hidden="true" />
          <img
            src={IMAGES.elderlyCouple}
            alt="Elderly couple sharing a quiet moment in a Penang residential care home"
            width="800"
            height="600"
            loading="lazy"
            className="relative rounded-[2rem] object-cover w-full h-[380px] sm:h-[460px] shadow-xl"
          />
        </Reveal>
      </div>
    </section>

    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="How It Works"
          title="Eight Steps From First Message to Move-In and Beyond"
          copy="A transparent, step-by-step arrangement process that gives your family peace of mind at every stage — including after move-in."
        />
        <div className="mt-10">
          <SevenSteps />
        </div>
        <Reveal delay={0.1} className="mt-8 text-center">
          <a href="/contact/" data-testid="steps-full-process-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:text-brand-gold transition-colors">
            See the full process on our Contact page <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <Reveal>
        <div className="grid items-center gap-10 rounded-3xl border border-slate-100 bg-white p-8 sm:p-12 shadow-[0_20px_60px_rgb(7,91,89,0.08)] lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">Cost Guide</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-brand-charcoal">
              What Shapes Nursing Home Fees in Penang?
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Fees depend on room type, care dependency, nursing requirements, location, deposits and additional
              services. Learn how to compare quotes fairly — and what to get in writing before you commit.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href="/pricing-guide/"
                data-testid="cost-guide-link"
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-6 py-3 text-sm font-semibold text-white hover:bg-brand-teal hover:-translate-y-0.5 transition-all duration-300"
              >
                Read the Cost Guide <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/caregiver-packages/"
                data-testid="caregiver-packages-link"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand-teal px-6 py-3 text-sm font-semibold text-brand-teal hover:bg-brand-teal hover:text-white hover:-translate-y-0.5 transition-all duration-300"
              >
                View Caregiver Packages <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {["Room type & privacy", "Level of care dependency", "Nursing requirements", "Location in Penang", "Deposits & one-time fees", "Additional services"].map((f) => (
              <li key={f} className="rounded-xl bg-brand-tealLight px-4 py-3 text-sm font-medium text-brand-teal">{f}</li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>

    <section className="bg-white py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Common Questions"
          title="Nursing Home FAQs for Penang Families"
        />
        <Reveal delay={0.1} className="mt-10">
          <FaqAccordion faqs={FAQS} testid="homepage-faq" />
        </Reveal>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-teal grain px-6 py-14 sm:px-12 sm:py-16 text-center text-white">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-gold/25 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-coral/25 blur-3xl" aria-hidden="true" />
          <h2 data-testid="final-cta-heading" className="relative mx-auto max-w-2xl font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Need Help Finding a Nursing Home in Penang?
          </h2>
          <p className="relative mx-auto mt-4 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed">
            Tell us your preferred location, care requirements and budget. We will help you explore suitable options and
            coordinate the next steps.
          </p>
          <WhatsAppCTA testid="final-whatsapp-cta" message={WA_MESSAGES.freeAssistance} variant="gold" className="relative mt-8" />
        </div>
      </Reveal>
    </section>
  </main>
);

export default HomePage;
